import { useState } from 'react';
import { Card, ErrorBanner, Field, PageHeader, SearchCombobox } from '@zarya/ui';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { withEmphasis } from '@app/i18n/emphasis';
import { useInfraConfig } from './Infra.api';
import { InfraGroupTab } from './InfraGroupTab';
import { BadgeReadiness } from './InfraField';
import { groupSummary, groupLabel, visibleGroups } from './InfraModel';
import { useSiteOptions } from '../Sites/Sites.api';
import s from './Infra.module.css';

/**
 * T20 — Infraestrutura (T139).
 *
 * A tela inteira é **gerada** de `getInfraConfig` (FR-018): abas, campos, tipos,
 * obrigatórios, somente-leitura e avisos vêm do que o executor publica. Não há
 * lista própria de campos em lugar nenhum deste módulo — se o middleware ganhar
 * um grupo novo, ele aparece como aba sem uma linha de código.
 */
export function InfraPage() {
  // Vazio em tenant single-site (`multi` falso do servidor), e aí NADA muda nesta
  // tela — a revelação progressiva da P1, num lugar só: com um cluster, a palavra
  // «site» não aparece em T20. Ver `useSiteOptions`.
  const sites = useSiteOptions();
  const [site, setSite] = useState<string | null>(null);

  // `?? undefined` porque «nenhum escolhido» é o site PADRÃO, e o padrão viaja
  // como parâmetro ausente — não como `null` na query.
  const config = useInfraConfig(site ?? undefined);
  const [activeTab, setAbaAtiva] = useState<string | null>(null);

  // Só as abas sem a marca `hidden` (aim-o3r.14): ver `visibleGroups`.
  const grupos = visibleGroups(config.data);
  const ativa = activeTab ?? grupos[0]?.name ?? null;
  const grupo = grupos.find((g) => g.name === ativa);

  return (
    <>
      <PageHeader
        title={t('infrastructure.page.title')}
        tip={t('infrastructure.page.tip')}
        icon="database"
        updatedLabel={
          config.data
            ? [
                config.data.middleware_version
                  ? t('infrastructure.page.middlewareVersion', {
                      version: config.data.middleware_version,
                    })
                  : null,
                config.data.read_at
                  ? t('infrastructure.page.lastRead', {
                      when: dataHour(config.data.read_at) ?? '',
                    })
                  : null,
                config.data.written_at
                  ? t('infrastructure.page.lastWrite', {
                      when: dataHour(config.data.written_at) ?? '',
                    })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
      />

      {/*
        Os blocos da tela num contentor só: a distância entre eles é regra do PAI
        (`.blocos`), como em T21. Ver o comentário do CSS.
      */}
      <div className={s.blocos}>
        {sites.length > 0 ? (
          <Card title={t('infrastructure.site.title')}>
            <p className={s.carregando}>{t('infrastructure.site.body')}</p>
            <Field label={t('infrastructure.site.label')} help={t('infrastructure.site.help')}>
              {({ className: _unused, ...props }) => (
                <SearchCombobox
                  {...props}
                  label={t('infrastructure.site.label')}
                  options={sites.map((option) => ({
                    value: option.id,
                    label: option.slug,
                    mono: true,
                  }))}
                  value={site}
                  onChange={(escolhido) => {
                    setSite(escolhido ?? null);
                    // A aba volta ao início: os grupos são os que AQUELE executor
                    // publica, e um cluster pode publicar grupos que o outro não tem
                    // — manter a aba pelo nome mostraria «painel vazio» sem dizer por
                    // quê.
                    setAbaAtiva(null);
                  }}
                  placeholder={t('infrastructure.site.placeholder')}
                />
              )}
            </Field>
          </Card>
        ) : null}

        {config.isError ? (
          // FR-025: a configuração vem do executor; sem ele, a tela não se monta.
          <ErrorBanner
            error={config.error}
            title={t('infrastructure.readError')}
            onRetry={() => void config.refetch()}
          >
            <p className={s.dicaErro}>{withEmphasis(t('infrastructure.readError.hint'))}</p>
          </ErrorBanner>
        ) : null}

        {config.isLoading ? (
          <Card title={t('infrastructure.loading.title')}>
            <p className={s.carregando}>{t('infrastructure.loading.body')}</p>
          </Card>
        ) : null}

        {grupos.length > 0 ? (
          <>
            <div className={s.abas} role="tablist" aria-label={t('infrastructure.tabs.aria')}>
              {grupos.map((g) => {
                const { pronto, faltam } = groupSummary(g);
                const selecionada = g.name === ativa;
                return (
                  <button
                    key={g.name}
                    type="button"
                    role="tab"
                    id={`aba-${g.name}`}
                    aria-selected={selecionada}
                    aria-controls={`painel-${g.name}`}
                    className={[s.aba_botao, selecionada ? s.aba_ativa : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setAbaAtiva(g.name)}
                  >
                    {groupLabel(g)}
                    <BadgeReadiness pronto={pronto} faltam={faltam} />
                  </button>
                );
              })}
            </div>

            {grupo ? (
              <div role="tabpanel" id={`painel-${grupo.name}`} aria-labelledby={`aba-${grupo.name}`}>
                {/*
                  `key` só pelo nome do grupo: trocar de aba recria o formulário,
                  mas recarregar a configuração **não** — senão o resultado do
                  próprio salvar ("Grupo salvo", "Nada a salvar", o 422 com o campo)
                  seria desmontado junto com o componente, e o usuário não veria
                  nada acontecer. Quem realinha os valores com o efetivo é o
                  próprio `InfraGroupTab`.
                */}
                {/*
                  O `key` carrega o SITE além do grupo (S-e (2)): trocar de cluster
                  tem de recriar o formulário, senão os valores digitados para um
                  executor ficariam na tela do outro — e o Salvar os mandaria para
                  lá.
                */}
                <InfraGroupTab key={`${site ?? 'default'}:${grupo.name}`} grupo={grupo} siteId={site ?? undefined} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
